import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
    FiStar,
    FiThumbsUp,
    FiMessageSquare,
    FiFilter,
    FiTrendingUp,
    FiUser,
    FiSearch
} from 'react-icons/fi'
import { formatDate } from '@/utils/formatters'

interface Review {
    id: number
    clientName: string
    clientAvatar?: string
    gigTitle: string
    rating: number
    comment: string
    date: Date
    isPublic: boolean
    helpful: number
}

const ReviewsPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('')
    const [filterRating, setFilterRating] = useState('all')

    // Mock data - replace with actual API data
    const reviewsData = {
        averageRating: 4.8,
        totalReviews: 47,
        ratingDistribution: {
            5: 32,
            4: 12,
            3: 2,
            2: 1,
            1: 0
        }
    }

    const reviews: Review[] = [
        {
            id: 1,
            clientName: 'John Smith',
            clientAvatar: undefined,
            gigTitle: 'Logo Design for Tech Startup',
            rating: 5,
            comment: 'Absolutely fantastic work! The designer understood our vision perfectly and delivered beyond our expectations. The communication was excellent throughout the project.',
            date: new Date('2024-01-15'),
            isPublic: true,
            helpful: 8
        },
        {
            id: 2,
            clientName: 'Sarah Johnson',
            clientAvatar: undefined,
            gigTitle: 'Website Development',
            rating: 5,
            comment: 'Professional, timely, and high-quality work. Would definitely hire again!',
            date: new Date('2024-01-12'),
            isPublic: true,
            helpful: 12
        },
        {
            id: 3,
            clientName: 'Mike Chen',
            clientAvatar: undefined,
            gigTitle: 'Social Media Graphics Package',
            rating: 4,
            comment: 'Great designs and quick turnaround. Minor revisions were needed but overall very satisfied.',
            date: new Date('2024-01-08'),
            isPublic: true,
            helpful: 5
        },
        {
            id: 4,
            clientName: 'Emily Davis',
            clientAvatar: undefined,
            gigTitle: 'Mobile App UI Design',
            rating: 5,
            comment: 'Amazing attention to detail and creativity. The final product exceeded all expectations.',
            date: new Date('2024-01-05'),
            isPublic: true,
            helpful: 15
        },
        {
            id: 5,
            clientName: 'David Wilson',
            clientAvatar: undefined,
            gigTitle: 'Brand Identity Package',
            rating: 4,
            comment: 'Good work overall. Communication could have been better but the end result was solid.',
            date: new Date('2024-01-02'),
            isPublic: false,
            helpful: 3
        }
    ]

    const filteredReviews = reviews.filter(review => {
        const matchesSearch = review.gigTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
            review.clientName.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesRating = filterRating === 'all' || review.rating.toString() === filterRating
        return matchesSearch && matchesRating
    })

    const renderStars = (rating: number, size = 'w-4 h-4') => {
        return Array.from({ length: 5 }, (_, index) => (
            <FiStar
                key={index}
                className={`${size} ${index < rating
                    ? 'text-yellow-400 fill-current'
                    : 'text-gray-300 dark:text-gray-600'
                    }`}
            />
        ))
    }

    return (
        <>
            <Helmet>
                <title>Reviews - SideGO</title>
                <meta name="description" content="View and manage your client reviews and ratings" />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Reviews & Ratings
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Track your client feedback and maintain your reputation
                    </p>
                </div>

                {/* Rating Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Overall Rating */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                                    {reviewsData.averageRating}
                                </span>
                                <FiStar className="w-8 h-8 text-yellow-400 fill-current" />
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">
                                Average Rating
                            </p>
                            <p className="text-gray-500 dark:text-gray-500 text-sm">
                                Based on {reviewsData.totalReviews} reviews
                            </p>
                        </div>
                    </div>

                    {/* Rating Distribution */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                            Rating Distribution
                        </h3>
                        <div className="space-y-2">
                            {[5, 4, 3, 2, 1].map((rating) => (
                                <div key={rating} className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-400 w-4">
                                        {rating}
                                    </span>
                                    <FiStar className="w-3 h-3 text-yellow-400 fill-current" />
                                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                        <div
                                            className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                                            style={{
                                                width: `${(reviewsData.ratingDistribution[rating as keyof typeof reviewsData.ratingDistribution] / reviewsData.totalReviews) * 100}%`
                                            }}
                                        />
                                    </div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400 w-6">
                                        {reviewsData.ratingDistribution[rating as keyof typeof reviewsData.ratingDistribution]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                            Review Stats
                        </h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FiThumbsUp className="w-4 h-4 text-green-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        Positive Reviews
                                    </span>
                                </div>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                    {Math.round(((reviewsData.ratingDistribution[5] + reviewsData.ratingDistribution[4]) / reviewsData.totalReviews) * 100)}%
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FiTrendingUp className="w-4 h-4 text-blue-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        Recent Trend
                                    </span>
                                </div>
                                <span className="text-green-600 dark:text-green-400 font-semibold">
                                    +0.2 this month
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FiMessageSquare className="w-4 h-4 text-purple-500" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        Avg Response Time
                                    </span>
                                </div>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                    2 hours
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search reviews by gig title or client name..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <FiFilter className="w-5 h-5 text-gray-400" />
                            <select
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                value={filterRating}
                                onChange={(e) => setFilterRating(e.target.value)}
                            >
                                <option value="all">All Ratings</option>
                                <option value="5">5 Stars</option>
                                <option value="4">4 Stars</option>
                                <option value="3">3 Stars</option>
                                <option value="2">2 Stars</option>
                                <option value="1">1 Star</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Reviews List */}
                <div className="space-y-6">
                    {filteredReviews.length > 0 ? (
                        filteredReviews.map((review) => (
                            <div
                                key={review.id}
                                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                            {review.clientAvatar ? (
                                                <img
                                                    src={review.clientAvatar}
                                                    alt={review.clientName}
                                                    className="h-10 w-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <FiUser className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 dark:text-white">
                                                {review.clientName}
                                            </h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {review.gigTitle}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-center gap-1 mb-1">
                                            {renderStars(review.rating)}
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-500">
                                            {formatDate(review.date.toISOString())}
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                        {review.comment}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                            <FiThumbsUp className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm text-gray-500 dark:text-gray-500">
                                                {review.helpful} found helpful
                                            </span>
                                        </div>
                                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${review.isPublic
                                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                            : 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200'
                                            }`}>
                                            {review.isPublic ? 'Public' : 'Private'}
                                        </span>
                                    </div>
                                    <button className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                                        Reply
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-12">
                            <FiMessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                No reviews found
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                {searchTerm || filterRating !== 'all'
                                    ? 'Try adjusting your search or filter criteria.'
                                    : 'Complete some gigs to start receiving reviews from clients.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ReviewsPage